/**
 * scenarioManager.ts - Persistent library of named reservoir scenarios.
 *
 *   - LOCAL_SCENARIOS_KEY (localStorage) - persistent library across sessions
 *   - TRAIN_SCENARIO_KEY  (sessionStorage) - currently loaded scenario that
 *                                            TrainSummaryPage reads on mount
 *
 * Saved scenarios come from four sources:
 *   - 'preset'    : shipped defaults (Barcombe default, etc.)
 *   - 'benchmark' : imported from BenchmarkSitesPage (Loch Leven, M-ggelsee-)
 *   - 'perfect'   : pushed by PerfectScenarioPage
 *   - 'custom'    : user-saved adjustments
 *
 * Any field of ScenarioInputs may be overridden by the user; we keep the full
 * 22-parameter object so the train summary page can re-compute every stage.
 */

import { BENCHMARK_SITES } from '@/lib/benchmarkSites';

export const LOCAL_SCENARIOS_KEY = 'rb_scenarios_v1';
export const TRAIN_SCENARIO_KEY = 'rb_train_scenario_params_v1';

export interface ScenarioInputs {
  area_m2: number;
  depth_mean_m: number;
  depth_max_m: number;
  chl_ugL: number;
  nom_mgL: number;
  tn_mgL: number;
  tp_mgL: number;
  pH: number;
  surfaceTempC: number;
  hypoTempC: number;
  stratified: boolean;
  fCyano: number;
  windMs: number;
  doSurface_mgL: number;
  doHypo_mgL: number;
  orp_mV: number;
  N_brunt: number;
  feII_mgL: number;
  mnII_mgL: number;
  diffuserQair_Ls: number;
  diffuserDepth_m: number;
  bubble_d_mm: number;
}

export type ScenarioSource = 'preset' | 'benchmark' | 'perfect' | 'custom';

export interface SavedScenario {
  id: string;
  name: string;
  description?: string;
  source: ScenarioSource;
  /** Optional benchmark-site id this scenario originated from. */
  sourceSiteId?: string;
  /** Optional preferred module-train id. */
  trainId?: string;
  inputs: ScenarioInputs;
  createdAt: number;
  updatedAt: number;
}

// ------------------------------------------------------------------------
// Built-in presets - always available even if localStorage is wiped
// ------------------------------------------------------------------------

// Barcombe Reservoir, East Sussex - SEW Ouse Supply Scheme.
// Geometry derived from the published Shoreline Surveys Ltd J2020 / drawing
// 01v2 bathymetry (09 Jan 2023, ODN Newlyn 8.79 m water level, scale 1:1000):
//   surface area 35 319 m^2 (3.53 ha, shoelace integral of survey polygon)
//   mean depth   2.72 m     (volume-weighted integral over polygon)
//   max depth    4.79 m     (NE deepest basin sounding)
//   volume       ~ 96 040 m^3 (area x mean depth)
const BARCOMBE_DEFAULT: ScenarioInputs = {
  area_m2: 35319, depth_mean_m: 2.72, depth_max_m: 4.79,
  chl_ugL: 30, nom_mgL: 8, tn_mgL: 1.2, tp_mgL: 0.08, pH: 7.6,
  surfaceTempC: 19, hypoTempC: 11, stratified: true, fCyano: 0.45,
  windMs: 2.5, doSurface_mgL: 8.5, doHypo_mgL: 1.5, orp_mV: 50,
  N_brunt: 0.012, feII_mgL: 0.6, mnII_mgL: 0.45,
  diffuserQair_Ls: 2.5, diffuserDepth_m: 4.0, bubble_d_mm: 3.0,
};

export const BUILT_IN_PRESETS: SavedScenario[] = [
  {
    id: 'preset_barcombe_default',
    name: 'Barcombe - default',
    description: 'Project default for Barcombe Reservoir (SE England, 34 000 m-, 4.5 m mean, eutrophic polymictic).',
    source: 'preset',
    trainId: 'shallow_uk_eutrophic',
    inputs: BARCOMBE_DEFAULT,
    createdAt: 0, updatedAt: 0,
  },
  {
    id: 'preset_barcombe_summer_worstcase',
    name: 'Barcombe - summer worst-case',
    description: 'August heatwave + bloom peak: chl-a 70 -g/L, cyano 65 %, hypolimnion anoxic, ORP collapsed.',
    source: 'preset',
    trainId: 'shallow_uk_eutrophic',
    inputs: {
      ...BARCOMBE_DEFAULT,
      chl_ugL: 70, fCyano: 0.65, surfaceTempC: 24, hypoTempC: 14,
      stratified: true, doHypo_mgL: 0.4, orp_mV: -80,
      mnII_mgL: 1.2, feII_mgL: 1.6, windMs: 1.5,
    },
    createdAt: 0, updatedAt: 0,
  },
  {
    id: 'preset_barcombe_winter',
    name: 'Barcombe - winter baseline',
    description: 'January reference: fully mixed, low chl-a, high DO, low surface temperature.',
    source: 'preset',
    trainId: 'shallow_uk_eutrophic',
    inputs: {
      ...BARCOMBE_DEFAULT,
      chl_ugL: 4, fCyano: 0.1, surfaceTempC: 5, hypoTempC: 4.5,
      stratified: false, doHypo_mgL: 11, doSurface_mgL: 11.5,
      orp_mV: 280, mnII_mgL: 0.02, feII_mgL: 0.05,
    },
    createdAt: 0, updatedAt: 0,
  },
];

// ------------------------------------------------------------------------
// Storage helpers
// ------------------------------------------------------------------------

export function loadAllScenarios(): SavedScenario[] {
  // Built-ins always first
  const presets = [...BUILT_IN_PRESETS];

  // Benchmark sites - auto-expose them as benchmark-source scenarios
  const benchmarks: SavedScenario[] = BENCHMARK_SITES.map(s => ({
    id: `benchmark_${s.id}`,
    name: `${s.name} (${s.country})`,
    description: s.similarityRationale,
    source: 'benchmark' as ScenarioSource,
    sourceSiteId: s.id,
    trainId: s.recommendedTrains[0],
    inputs: { ...s.inputs },     // strip extra fields
    createdAt: 0, updatedAt: 0,
  }));

  // User-saved custom scenarios
  let custom: SavedScenario[] = [];
  try {
    const raw = localStorage.getItem(LOCAL_SCENARIOS_KEY);
    if (raw) custom = JSON.parse(raw) as SavedScenario[];
  } catch (e) {
    console.warn('Could not parse saved scenarios:', e);
  }

  return [...presets, ...benchmarks, ...custom];
}

export function loadCustomScenarios(): SavedScenario[] {
  try {
    const raw = localStorage.getItem(LOCAL_SCENARIOS_KEY);
    return raw ? (JSON.parse(raw) as SavedScenario[]) : [];
  } catch {
    return [];
  }
}

export function saveCustomScenario(s: SavedScenario): void {
  const all = loadCustomScenarios();
  const idx = all.findIndex(x => x.id === s.id);
  if (idx >= 0) all[idx] = s;
  else all.push(s);
  localStorage.setItem(LOCAL_SCENARIOS_KEY, JSON.stringify(all));
}

export function deleteCustomScenario(id: string): void {
  const all = loadCustomScenarios().filter(s => s.id !== id);
  localStorage.setItem(LOCAL_SCENARIOS_KEY, JSON.stringify(all));
}

/** Push a scenario into sessionStorage so the Train Summary picks it up. */
export function activateScenario(s: SavedScenario): void {
  sessionStorage.setItem(TRAIN_SCENARIO_KEY, JSON.stringify({
    siteId: s.sourceSiteId ?? s.id,
    siteName: s.name,
    inputs: s.inputs,
    timestamp: Date.now(),
    scenarioId: s.id,
  }));
}

/** Read the currently active scenario from sessionStorage. */
export function getActiveScenario(): { siteName: string; siteId: string; inputs: ScenarioInputs; scenarioId?: string } | null {
  try {
    const raw = sessionStorage.getItem(TRAIN_SCENARIO_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** Wipe the active scenario. */
export function clearActiveScenario(): void {
  sessionStorage.removeItem(TRAIN_SCENARIO_KEY);
}

/** Build a fresh custom scenario from a set of inputs. */
export function makeCustomScenario(name: string, inputs: ScenarioInputs, opts?: {
  description?: string; trainId?: string;
}): SavedScenario {
  const now = Date.now();
  return {
    id: `custom_${now}_${Math.random().toString(36).slice(2, 8)}`,
    name,
    description: opts?.description,
    source: 'custom',
    trainId: opts?.trainId,
    inputs: { ...inputs },
    createdAt: now,
    updatedAt: now,
  };
}

/** Diff two scenarios - returns keys that differ with both values. */
export function diffScenarios(a: ScenarioInputs, b: ScenarioInputs): Array<{ key: keyof ScenarioInputs; a: number | boolean; b: number | boolean }> {
  const keys = Object.keys(a) as Array<keyof ScenarioInputs>;
  const out: Array<{ key: keyof ScenarioInputs; a: number | boolean; b: number | boolean }> = [];
  for (const k of keys) {
    if (a[k] !== b[k]) out.push({ key: k, a: a[k], b: b[k] });
  }
  return out;
}
